import { say } from "../../words.ts";
import type { FeedbackAccess } from "./send.ts";

/**
 * The Feedback form's bounds and the wording that takes a decision: which sentence a paused or removed account
 * reads, and how its moment is written. The words are the language files' (`feedback.*` in `odyssey-lang`).
 */

/**
 * The most characters a title and a description hold, a player's and a staff member's: the gateway's own bounds
 * (`feedback-store.ts`), so the form stops the typing where the gateway would clip it.
 */
export const FEEDBACK_FIELD_CHARS = { player: { title: 120, description: 4000 }, staff: { title: 200, description: 20_000 } } as const;

/**
 * How a moment is written for a paused account: the time alone when it is today, the date and the time otherwise,
 * on the reader's own clock and in the reader's own locale.
 */
export interface MomentWords {
  readonly time: (at: number) => string;
  readonly dateTime: (at: number) => string;
}

/**
 * The page's own way of writing a moment.
 */
export const LOCAL_MOMENT: MomentWords = {
  time: (at) => new Date(at).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }),
  dateTime: (at) => new Date(at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }),
};

/**
 * Whether two moments fall on the same day of the reader's calendar.
 *
 * @param a One moment, in milliseconds.
 * @param b The other.
 * @returns True for the same local day.
 */
function sameDay(a: number, b: number): boolean {
  const one = new Date(a);
  const other = new Date(b);
  return one.getFullYear() === other.getFullYear() && one.getMonth() === other.getMonth() && one.getDate() === other.getDate();
}

/**
 * What the menu says beside a disabled Feedback entry.
 *
 * @param access The account's access.
 * @param now The page's clock, in milliseconds.
 * @param words Writes a moment; the page's own unless a test gives another.
 * @returns The sentence, or null while feedback is open.
 * @example
 * accessSentence({ state: "off" }, Date.now());
 * // "Feedback is turned off for this account. To ask for it back, contact the team through odysseytales.net."
 */
export function accessSentence(access: FeedbackAccess, now: number, words: MomentWords = LOCAL_MOMENT): string | null {
  if (access.state === "open") {
    return null;
  }
  if (access.state === "off") {
    return say("feedback.off");
  }
  return sameDay(access.until, now) ? say("feedback.pausedToday", { time: words.time(access.until) }) : say("feedback.pausedLater", { when: words.dateTime(access.until) });
}
